import { inject, Injectable } from '@angular/core';
import {
  BasinStyle,
  BasinStyleOption,
  BasinTypeOption,
  RowConfiguration,
  WidthOption,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-validation.models';
import {
  BathroomCompositionDetailDto,
  CompositionRowDto,
  BasinType,
  CompositionConfigurationDto,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-composition-dtos';
import { ProductAttribute } from 'src/app/models/configurators-2d/tunnel/bathroom/bathroom-tunnel-dtos';
import { BathroomValidationService } from './bathroom-validation.service';
import { KENTIA_RANGE_ID, KentiaConstraints } from './validation-rules';
import { BathroomConstraintDefaults } from 'src/app/consts/configurator-2d/mobile/bathroom/BathroomConstraintKeys.consts';

/**
 * Résultat de la vérification de basculement automatique Plan vasque → Vasque à poser
 */
export interface AutoSwitchResult {
  shouldSwitch: boolean;
  message?: string;
}

/**
 * Options disponibles avec informations de basculement automatique
 */
export interface AvailableOptionsResult {
  basinTypes: BasinTypeOption[];
  basinStyles: BasinStyleOption[];
  globalWidths: WidthOption[];
  allowOffset: boolean;
  dimensionDisplayType: 'global' | 'perRow';
  topRowWidths: WidthOption[];
  bottomRowWidths: WidthOption[];
  widthCombinations?: Map<number, number[]>;
  autoSwitch?: AutoSwitchResult;
}

@Injectable({
  providedIn: 'root',
})
export class BathroomOptionsService {
  private readonly validationService = inject(BathroomValidationService);

  // ============================================
  // Parsing des types autorisés
  // ============================================

  /**
   * Parse la propriété allowedBasinTypes de la composition
   * @returns Tableau de ProductAttribute autorisés, ou tableau vide si non défini
   */
  private parseAllowedBasinTypes(allowedBasinTypesJson?: string): ProductAttribute[] {
    if (!allowedBasinTypesJson) {
      return [];
    }
    try {
      return JSON.parse(allowedBasinTypesJson) as ProductAttribute[];
    } catch {
      console.warn('Impossible de parser allowedBasinTypes:', allowedBasinTypesJson);
      return [];
    }
  }

  /**
   * Parse la propriété widthCombinations de la composition
   * @returns Map<number, number[]> des combinaisons de largeurs autorisées
   */
  private parseWidthCombinations(combinationsString?: string): Map<number, number[]> {
    if (!combinationsString) {
      return new Map();
    }
    try {
      const parsed = JSON.parse(combinationsString);
      const map = new Map<number, number[]>();
      for (const key in parsed) {
        if (Object.prototype.hasOwnProperty.call(parsed, key)) {
          map.set(Number(key), parsed[key]);
        }
      }
      return map;
    } catch {
      console.warn('Impossible de parser widthCombinations:', combinationsString);
      return new Map();
    }
  }
  /**
   * Vérifie si le plan vasque est autorisé par la composition (via allowedBasinTypes)
   */
  private isPlanVasqueAllowedByComposition(allowedBasinTypes: ProductAttribute[]): boolean {
    // Si tableau vide, les deux styles sont autorisés par défaut
    if (allowedBasinTypes.length === 0) {
      return true;
    }
    // Vérifier si WashbasinTop (1) est dans la liste
    return allowedBasinTypes.includes(ProductAttribute.WashbasinTop);
  }

  /**
   * Vérifie si la vasque à poser est autorisée par la composition (via allowedBasinTypes)
   */
  private isCountertopBasinAllowedByComposition(allowedBasinTypes: ProductAttribute[]): boolean {
    // Si tableau vide, les deux styles sont autorisés par défaut
    if (allowedBasinTypes.length === 0) {
      return true;
    }
    // Vérifier si CountertopBasin (7) est dans la liste
    return allowedBasinTypes.includes(ProductAttribute.CountertopBasin);
  }

  /**
   * Détermine le style de vasque par défaut en fonction des options disponibles.
   * Si un seul style est disponible, il est automatiquement sélectionné.
   * Si plusieurs styles sont disponibles, privilégie "Vasque à poser" par défaut.
   */
  getDefaultBasinStyle(basinStyles: BasinStyleOption[]): BasinStyle {
    // Filtrer les styles disponibles (available = true)
    const availableStyles = basinStyles.filter(style => style.available);

    // Si un seul style est disponible, le retourner
    if (availableStyles.length === 1) {
      return availableStyles[0].value;
    }

    // Si "Vasque à poser" est disponible, la privilégier par défaut
    const countertopBasin = availableStyles.find(s => s.value === BasinStyle.CountertopBasin);
    if (countertopBasin) {
      return BasinStyle.CountertopBasin;
    }

    // Sinon retourner le premier style disponible, ou "Plan vasque" par défaut
    return availableStyles[0]?.value || BasinStyle.WashbasinTop;
  }

  /**
   * Détermine le type de vasque par défaut (Simple/Double) en fonction des options disponibles.
   * Si le type actuel n'est plus disponible, retourne un type valide.
   * @param basinTypes Les options de type de vasque disponibles
   * @param currentType Le type actuellement sélectionné (optionnel)
   * @returns Le type de vasque à utiliser
   */
  getDefaultBasinType(basinTypes: BasinTypeOption[], currentType?: BasinType): BasinType {
    // Si le type actuel est toujours disponible, le conserver
    if (currentType) {
      const currentOption = basinTypes.find(t => t.value === currentType);
      if (currentOption?.available) {
        return currentType;
      }
    }

    // Sinon, retourner le premier type disponible (privilégier "Simple")
    const simpleOption = basinTypes.find(t => t.value === BasinType.Simple);
    if (simpleOption?.available) {
      return BasinType.Simple;
    }

    const doubleOption = basinTypes.find(t => t.value === BasinType.Double);
    if (doubleOption?.available) {
      return BasinType.Double;
    }

    // Par défaut, retourner "Simple"
    return BasinType.Simple;
  }

  /**
   * Vérifie si le type de vasque actuel est toujours valide
   * @returns true si le type actuel est disponible, false sinon
   */
  isBasinTypeValid(basinTypes: BasinTypeOption[], currentType: BasinType): boolean {
    const option = basinTypes.find(t => t.value === currentType);
    return option?.available ?? false;
  }

  // ============================================
  // Contraintes
  // ============================================

  /**
   * Récupère les contraintes depuis le ValidationService ou utilise les valeurs par défaut
   */
  getConstraints(rangeId: string = KENTIA_RANGE_ID): KentiaConstraints {
    const constraints = this.validationService.getConstraintsForRange(rangeId);
    return (
      constraints || {
        maxToiletTopWidth: BathroomConstraintDefaults.MAX_TOILET_TOP_WIDTH,
        maxBasinPlanWidth: BathroomConstraintDefaults.MAX_BASIN_PLAN_WIDTH,
        doubleBasinWidth: BathroomConstraintDefaults.DOUBLE_BASIN_WIDTH,
        minFirstRowWidth: BathroomConstraintDefaults.MIN_FIRST_ROW_WIDTH,
        exceptionBasinPlanWidth: BathroomConstraintDefaults.EXCEPTION_BASIN_PLAN_WIDTH,
      }
    );
  }

  // ============================================
  // Calcul des largeurs disponibles pour lignes
  // ============================================

  /**
   * Vérifie si une largeur est disponible pour une ligne entière
   */
  isRowWidthAvailable(
    width: number,
    row: CompositionRowDto,
    composition: BathroomCompositionDetailDto,
    basinStyle?: BasinStyle,
    rangeId?: string
  ): { available: boolean; reason?: string } {
    const constraints = this.getConstraints(rangeId);
    const config = composition.compositionConfiguration;

    // Contrainte : vérifier que la largeur est dans les dimensions possibles
    let topWidths = this.parseRowWidths(config?.topRowWidths);
    const bottomWidths = this.parseRowWidths(config?.bottomRowWidths);

    // Fallback sur bottomWidths si topWidths est vide
    if (topWidths.length === 0) {
      topWidths = bottomWidths;
    }

    if (row.index === 0 && topWidths.length > 0 && !topWidths.includes(width)) {
      return {
        available: false,
        reason: `Cette dimension n'est pas disponible pour cette composition`,
      };
    }

    if (row.index === 1 && bottomWidths.length > 0 && !bottomWidths.includes(width)) {
      return {
        available: false,
        reason: `Cette dimension n'est pas disponible pour cette composition`,
      };
    }

    // Contrainte : la première ligne doit avoir une largeur minimum
    if (row.index === 0 && width < constraints.minFirstRowWidth) {
      return {
        available: false,
        reason: `La largeur minimum de la première ligne est de ${constraints.minFirstRowWidth}cm`,
      };
    }

    // La limite dépend du style de vasque (uniquement pour la première ligne)
    if (row.index === 0 && basinStyle) {
      const maxWidth =
        basinStyle === BasinStyle.WashbasinTop ? constraints.maxBasinPlanWidth : constraints.maxToiletTopWidth;

      if (width > maxWidth) {
        const styleLabel = basinStyle === BasinStyle.WashbasinTop ? BasinStyle.WashbasinTop : 'plan de toilette';
        return {
          available: false,
          reason: `Largeur totale (${width}cm) dépasse la taille max du ${styleLabel} (${maxWidth}cm)`,
        };
      }

      // Contrainte sur la longueur du meuble et la possibilité de choisir un plan vasque
      if (
        config?.hasElementRight &&
        basinStyle === BasinStyle.WashbasinTop &&
        width !== constraints.exceptionBasinPlanWidth
      ) {
        // Si la vasque à poser est autorisée, on autorise la dimension pour permettre le basculement automatique
        const allowedBasinTypes = this.parseAllowedBasinTypes(config.allowedBasinTypes);
        if (this.isCountertopBasinAllowedByComposition(allowedBasinTypes)) {
          return { available: true };
        }

        return {
          available: false,
          reason: `La largeur de la ligne doit faire exactement ${constraints.exceptionBasinPlanWidth}cm pour utiliser un plan vasque`,
        };
      }
    }

    return { available: true };
  }

  /**
   * Calcule les options de dimensions globales
   * Utilise topRowWidths pour les dimensions disponibles, sinon bottomRowWidths comme fallback
   */
  calculateGlobalWidthOptions(
    composition: BathroomCompositionDetailDto,
    basinStyle?: BasinStyle,
    rangeId?: string
  ): WidthOption[] {
    const config = composition.compositionConfiguration;
    if (!config) return [];

    let widths = this.parseRowWidths(config.topRowWidths);
    // Fallback sur bottomRowWidths si topRowWidths est vide
    if (widths.length === 0) {
      widths = this.parseRowWidths(config.bottomRowWidths);
    }
    if (widths.length === 0) return [];

    const firstRow = config.rows?.find(r => r.index === 0);
    if (!firstRow) return [];

    return widths.map(width => {
      const validation = this.isRowWidthAvailable(width, firstRow, composition, basinStyle, rangeId);
      return {
        value: width,
        available: validation.available,
        reason: validation.reason,
      };
    });
  }

  /**
   * Calcule les options de dimensions pour une rangée spécifique
   * Utilise topRowWidths ou bottomRowWidths selon l'index de la rangée
   * Si topRowWidths est vide pour index 0, utilise bottomRowWidths comme fallback
   */
  calculateRowWidthOptions(
    composition: BathroomCompositionDetailDto,
    rowIndex: number,
    basinStyle?: BasinStyle,
    rangeId?: string
  ): WidthOption[] {
    const config = composition.compositionConfiguration;
    if (!config) return [];

    const row = config.rows?.find(r => r.index === rowIndex);
    if (!row) return [];

    // Utiliser topRowWidths pour index 0, bottomRowWidths pour index 1
    let availableWidths: number[];
    if (rowIndex === 0) {
      availableWidths = this.parseRowWidths(config.topRowWidths);
      // Fallback sur bottomRowWidths si topRowWidths est vide
      if (availableWidths.length === 0) {
        availableWidths = this.parseRowWidths(config.bottomRowWidths);
      }
    } else {
      availableWidths = this.parseRowWidths(config.bottomRowWidths);
    }

    if (availableWidths.length === 0) return [];

    return availableWidths.map(width => {
      const validation = this.isRowWidthAvailable(width, row, composition, basinStyle, rangeId);
      return {
        value: width,
        available: validation.available,
        reason: validation.reason,
      };
    });
  }

  // ============================================
  // Calcul des options disponibles
  // ============================================

  /**
   * Vérifie si le plan vasque nécessite un basculement automatique vers vasque à poser
   * Hiérarchie des règles :
   * 1. allowedBasinTypes : Si plan vasque non autorisé → basculer
   * 2. hasElementRight : Si largeur !== exceptionBasinPlanWidth → basculer
   * 3. isFirstColumnNiche : Si première colonne est une niche → basculer
   * @returns AutoSwitchResult avec shouldSwitch et message explicatif
   */
  shouldAutoSwitchToCountertopBasin(
    composition: BathroomCompositionDetailDto,
    currentBasinStyle?: BasinStyle
  ): AutoSwitchResult {
    // Si le style actuel n'est pas "Plan vasque", pas de basculement nécessaire
    if (currentBasinStyle !== BasinStyle.WashbasinTop) {
      return { shouldSwitch: false };
    }

    const config = composition.compositionConfiguration;
    const constraints = this.getConstraints(config?.rangeId);

    // Vérifier si la vasque à poser est autorisée (sinon on ne peut pas basculer)
    const allowedBasinTypes = this.parseAllowedBasinTypes(config?.allowedBasinTypes);
    const canSwitchToCountertop = this.isCountertopBasinAllowedByComposition(allowedBasinTypes);

    // ===== RÈGLE 1 : allowedBasinTypes =====
    if (!this.isPlanVasqueAllowedByComposition(allowedBasinTypes)) {
      if (canSwitchToCountertop) {
        return {
          shouldSwitch: true,
          message: "Cette composition n'autorise que les vasques à poser",
        };
      }
      // Cas impossible en théorie : ni plan vasque ni vasque à poser autorisés
      return { shouldSwitch: false };
    }

    const firstRow = config?.rows?.find(r => r.index === 0);
    const rowWidth = firstRow?.width || 0;
    const hasElementRight = config?.hasElementRight;

    // ===== RÈGLE 2 : hasElementRight =====
    if (hasElementRight && rowWidth !== constraints.exceptionBasinPlanWidth) {
      if (canSwitchToCountertop) {
        return {
          shouldSwitch: true,
          message: `Dans cette composition, le plan vasque n'est disponible que lorsque la première rangée fait ${constraints.exceptionBasinPlanWidth}cm`,
        };
      }
      return { shouldSwitch: false };
    }

    // ===== RÈGLE 3 : isFirstColumnNiche =====
    // Trier les colonnes par index pour identifier correctement la colonne la plus à gauche
    const sortedColumns = [...(firstRow?.columns || [])].sort((a, b) => a.index - b.index);
    const firstColumn = sortedColumns[0];
    const isFirstColumnNiche = firstColumn?.product?.attribute === ProductAttribute.Niche;

    if (isFirstColumnNiche) {
      if (canSwitchToCountertop) {
        return {
          shouldSwitch: true,
          message:
            "Le plan vasque n'est pas disponible car la première colonne est une niche (évacuation d'eau à gauche)",
        };
      }
      return { shouldSwitch: false };
    }

    return { shouldSwitch: false };
  }

  /**
   * Calcule la disponibilité du plan vasque selon les règles métier
   * Hiérarchie des règles :
   * 1. allowedBasinTypes : Restriction au niveau de la composition
   * 2. hasElementRight : Contrainte structurelle (largeur === exceptionBasinPlanWidth)
   * 3. isFirstColumnNiche : Contrainte d'évacuation d'eau
   */
  private calculatePlanVasqueAvailability(
    composition: BathroomCompositionDetailDto,
    constraints: KentiaConstraints
  ): { available: boolean; reason?: string } {
    const config = composition.compositionConfiguration;

    // ===== RÈGLE 1 : allowedBasinTypes =====
    const allowedBasinTypes = this.parseAllowedBasinTypes(config?.allowedBasinTypes);
    if (!this.isPlanVasqueAllowedByComposition(allowedBasinTypes)) {
      return {
        available: false,
        reason: "Cette composition n'autorise que les vasques à poser",
      };
    }

    const firstRow = config?.rows?.find(r => r.index === 0);
    const rowWidth = firstRow?.width || 0;
    const hasElementRight = config?.hasElementRight;

    // ===== RÈGLE 2 : hasElementRight =====
    if (hasElementRight) {
      // Plan vasque disponible UNIQUEMENT si rowWidth === exceptionBasinPlanWidth (100cm)
      if (rowWidth !== constraints.exceptionBasinPlanWidth) {
        return {
          available: false,
          reason: `Dans cette composition, le plan vasque n'est disponible que lorsque la première rangée fait ${constraints.exceptionBasinPlanWidth}cm`,
        };
      }
    }

    // ===== RÈGLE 3 : isFirstColumnNiche =====
    // Trier les colonnes par index pour identifier correctement la colonne la plus à gauche
    const sortedColumns = [...(firstRow?.columns || [])].sort((a, b) => a.index - b.index);
    const firstColumn = sortedColumns[0];
    const isFirstColumnNiche = firstColumn?.product?.attribute === ProductAttribute.Niche;

    if (isFirstColumnNiche) {
      return {
        available: false,
        reason: "Plan vasque impossible : la première colonne est une niche (l'évacuation d'eau se fait à gauche)",
      };
    }

    // Toutes les règles sont satisfaites
    return { available: true };
  }

  /**
   * Calcule la disponibilité de la vasque à poser selon les règles métier
   */
  private calculateCountertopBasinAvailability(composition: BathroomCompositionDetailDto): {
    available: boolean;
    reason?: string;
  } {
    const config = composition.compositionConfiguration;

    // ===== RÈGLE 1 : allowedBasinTypes =====
    const allowedBasinTypes = this.parseAllowedBasinTypes(config?.allowedBasinTypes);
    if (!this.isCountertopBasinAllowedByComposition(allowedBasinTypes)) {
      return {
        available: false,
        reason: "Cette composition n'autorise que les plans vasques",
      };
    }

    // La vasque à poser est toujours disponible si autorisée par la composition
    return { available: true };
  }

  /**
   * Calcule les options de type de vasque (Simple/Double) selon les règles métier
   */
  private calculateBasinTypes(
    composition: BathroomCompositionDetailDto,
    basinStyle: BasinStyle,
    constraints: KentiaConstraints
  ): BasinTypeOption[] {
    const config = composition.compositionConfiguration;

    // Règle : Si allowDoubleBasin est faux, seule la vasque Simple est autorisée
    if (!config?.allowDoubleBasin) {
      return [{ value: BasinType.Simple, label: 'Simple', available: true }];
    }

    const firstRow = config?.rows?.find(r => r.index === 0);

    // Calculer la largeur des niches
    const nicheWidth =
      firstRow?.columns?.reduce((sum, col) => {
        if (col.product?.attribute === ProductAttribute.Niche) return sum + col.width;
        return sum;
      }, 0) || 0;

    // Calculer la largeur sans les niches en se basant sur la largeur de la ligne
    const rowWidthWithoutNiche = (firstRow?.width || 0) - nicheWidth;

    if (basinStyle === BasinStyle.WashbasinTop) {
      // Règle Plan vasque :
      // - Si rowWidth === 120cm (maxBasinPlanWidth) → automatiquement Double
      // - Si rowWidth < 120cm → Simple uniquement
      const isMaxWidth = rowWidthWithoutNiche === constraints.maxBasinPlanWidth;

      return [
        {
          value: BasinType.Simple,
          label: 'Simple',
          available: !isMaxWidth,
          reason: isMaxWidth ? 'En 120cm, le plan vasque est automatiquement double' : undefined,
        },
        {
          value: BasinType.Double,
          label: 'Double',
          available: isMaxWidth,
          reason: !isMaxWidth ? 'Le plan vasque double nécessite exactement 120cm' : undefined,
        },
      ];
    }

    // Règle Vasque à poser :
    // - Simple : toujours disponible
    // - Double : disponible si au moins un tiroir (colonne non-niche) dans TOUTES les rangées fait >= doubleBasinWidth

    const canHaveDouble =
      composition.compositionConfiguration.allowDoubleBasin && firstRow.width >= constraints.doubleBasinWidth;
    let doubleReason: string | undefined;

    if (!canHaveDouble) {
      doubleReason = `Double vasque nécessite au moins un tiroir d'une largeur de ${constraints.doubleBasinWidth}cm minimum`;
    }

    return [
      { value: BasinType.Simple, label: 'Simple', available: true },
      {
        value: BasinType.Double,
        label: 'Double',
        available: canHaveDouble,
        reason: doubleReason,
      },
    ];
  }

  /**
   * Calcule toutes les options disponibles pour une composition
   */
  calculateAvailableOptions(
    composition: BathroomCompositionDetailDto,
    _currentBasinType: BasinType,
    currentBasinStyle?: BasinStyle,
    rowConfigurations?: RowConfiguration[]
  ): AvailableOptionsResult {
    const constraints = this.getConstraints(composition.compositionConfiguration.rangeId);
    const config = composition.compositionConfiguration;

    // Parser les types de vasque autorisés
    const allowedBasinTypes = this.parseAllowedBasinTypes(config?.allowedBasinTypes);

    // Vérifier quels styles sont autorisés par la composition
    const isPlanVasqueAllowed = this.isPlanVasqueAllowedByComposition(allowedBasinTypes);
    const isCountertopBasinAllowed = this.isCountertopBasinAllowedByComposition(allowedBasinTypes);

    // Calculer la disponibilité des styles de vasque (règles métier additionnelles)
    const planVasqueAvailability = this.calculatePlanVasqueAvailability(composition, constraints);
    const countertopBasinAvailability = this.calculateCountertopBasinAvailability(composition);

    // Filtrer les options de style de vasque selon allowedBasinTypes
    // Seuls les styles autorisés par la composition sont affichés
    const basinStyles: BasinStyleOption[] = [];

    if (isCountertopBasinAllowed) {
      basinStyles.push({
        value: BasinStyle.CountertopBasin,
        available: countertopBasinAvailability.available,
        reason: countertopBasinAvailability.reason,
      });
    }

    if (isPlanVasqueAllowed) {
      basinStyles.push({
        value: BasinStyle.WashbasinTop,
        available: planVasqueAvailability.available,
        reason: planVasqueAvailability.reason,
      });
    }

    // Déterminer le style effectif à utiliser
    const style = currentBasinStyle || this.getDefaultBasinStyle(basinStyles);

    // Vérifier si un basculement automatique est nécessaire
    const autoSwitch = this.shouldAutoSwitchToCountertopBasin(composition, currentBasinStyle);

    // Calculer les options de type de vasque selon le style effectif
    // Si un basculement est nécessaire, calculer pour "Vasque à poser"
    const effectiveStyle = autoSwitch.shouldSwitch ? BasinStyle.CountertopBasin : style;
    const basinTypes = this.calculateBasinTypes(composition, effectiveStyle, constraints);

    // allowOffset est déterminé par le type d'affichage (perRow = offset possible)
    const allowOffset = this.getDimensionDisplayType(composition.compositionConfiguration) === 'perRow';

    // Déterminer le type d'affichage des dimensions
    const dimensionDisplayType = this.getDimensionDisplayType(config);

    // Calculer les dimensions selon le type d'affichage
    let globalWidths: WidthOption[] = [];
    let topRowWidths: WidthOption[] = [];
    let bottomRowWidths: WidthOption[] = [];
    const widthCombinations = this.parseWidthCombinations(config.widthCombinations);

    if (dimensionDisplayType === 'perRow') {
      // Mode par rangée : chaque rangée a ses propres dimensions disponibles
      const allTopWidths = this.parseRowWidths(config.topRowWidths);
      const allBottomWidths = this.parseRowWidths(config.bottomRowWidths);

      const selectedTopWidth = rowConfigurations?.find(r => r.rowIndex === 0)?.selectedWidth;

      const row0 = config.rows?.find(r => r.index === 0);

      // Déterminer les largeurs compatibles pour le bas en fonction du haut
      const compatibleBottomWidths = selectedTopWidth ? widthCombinations.get(selectedTopWidth) : allBottomWidths;

      topRowWidths = allTopWidths.map(width => {
        let isAvailable = true;

        if (row0) {
          const validation = this.isRowWidthAvailable(width, row0, composition, currentBasinStyle, config.rangeId);
          isAvailable = validation.available;
        }

        return {
          value: width,
          available: isAvailable,
        };
      });

      bottomRowWidths = allBottomWidths.map(width => ({
        value: width,
        available: compatibleBottomWidths?.includes(width) ?? true,
      }));
    } else {
      // Mode global : utiliser topRowWidths comme dimensions globales
      globalWidths = this.calculateTopRowWidthOptions(config);

      // Valider les dimensions globales si elles sont définies
      if (globalWidths.length > 0) {
        const row0 = config.rows?.find(r => r.index === 0);
        if (row0) {
          globalWidths = globalWidths.map(opt => {
            const validation = this.isRowWidthAvailable(
              opt.value,
              row0,
              composition,
              currentBasinStyle,
              config.rangeId
            );
            return {
              ...opt,
              available: opt.available && validation.available,
              reason: validation.reason || opt.reason,
            };
          });
        }
      }

      // Si aucune dimension définie dans topRowWidths, fallback sur l'ancienne méthode
      if (globalWidths.length === 0) {
        globalWidths = this.calculateGlobalWidthOptions(
          composition,
          currentBasinStyle,
          composition.compositionConfiguration.rangeId
        );
      }
    }

    return {
      basinTypes,
      basinStyles,
      globalWidths,
      allowOffset,
      dimensionDisplayType,
      topRowWidths,
      bottomRowWidths,
      widthCombinations: widthCombinations.size > 0 ? widthCombinations : undefined,
      autoSwitch: autoSwitch.shouldSwitch ? autoSwitch : undefined,
    };
  }

  // ============================================
  // Calcul des offsets
  // ============================================

  /**
   * Calcule les offsets disponibles pour une ligne
   */
  calculateAvailableOffsets(row: CompositionRowDto): number[] {
    // L'offset ne s'applique qu'aux lignes après la première
    if (row.index === 0) {
      return [0];
    }
    // Options d'offset typiques en cm
    return [0, 5, 10, 15, 20, 25, 30];
  }

  /**
   * Calcule les contraintes d'offset pour une ligne
   */
  calculateOffsetConstraints(
    row: CompositionRowDto,
    allRows: CompositionRowDto[]
  ): { hasOffsetOption: boolean; minOffset: number; maxOffset: number } {
    // L'offset ne s'applique qu'aux lignes après la première
    if (row.index === 0) {
      return { hasOffsetOption: false, minOffset: 0, maxOffset: 0 };
    }

    // Récupérer la première rangée pour calculer le max
    const firstRow = allRows.find(r => r.index === 0);
    if (!firstRow) {
      return { hasOffsetOption: false, minOffset: 0, maxOffset: 0 };
    }

    // Min = -Max, Max = moitié de la largeur de la première rangée
    const maxOffset = Math.floor(firstRow.width / 2);
    const minOffset = -maxOffset;

    // Afficher l'option d'offset si c'est une rangée secondaire avec une plage valide
    const hasOffsetOption = maxOffset > 0;

    return { hasOffsetOption, minOffset, maxOffset };
  }

  // ============================================
  // Parsing des nouvelles propriétés de dimension
  // ============================================

  /**
   * Parse la chaîne topRowWidths en tableau de nombres
   * Format attendu: "[60, 100, 120]"
   */
  parseRowWidths(widthsString: string | undefined): number[] {
    if (!widthsString) return [];
    try {
      const parsed = JSON.parse(widthsString);
      if (Array.isArray(parsed)) {
        return parsed.filter(v => typeof v === 'number');
      }
      return [];
    } catch {
      return [];
    }
  }

  /**
   * Calcule les options de dimension pour la rangée haute à partir de topRowWidths
   * Si topRowWidths est vide, utilise bottomRowWidths comme fallback
   */
  calculateTopRowWidthOptions(config: CompositionConfigurationDto): WidthOption[] {
    let widths = this.parseRowWidths(config.topRowWidths);

    // Fallback sur bottomRowWidths si topRowWidths est vide
    if (widths.length === 0) {
      widths = this.parseRowWidths(config.bottomRowWidths);
    }

    return widths.map(width => ({
      value: width,
      available: true,
    }));
  }

  /**
   * Trouve la dimension disponible la plus proche d'une valeur cible
   * @param targetWidth La dimension actuelle/cible
   * @param availableWidths Les dimensions disponibles avec leur état de disponibilité
   * @returns La dimension disponible la plus proche, ou undefined si aucune n'est disponible
   */
  findClosestAvailableWidth(targetWidth: number, availableWidths: WidthOption[]): number | undefined {
    const enabledWidths = availableWidths.filter(w => w.available);
    if (enabledWidths.length === 0) return undefined;

    // Trier par distance à la valeur cible
    const sorted = [...enabledWidths].sort((a, b) => {
      const distA = Math.abs(a.value - targetWidth);
      const distB = Math.abs(b.value - targetWidth);
      return distA - distB;
    });

    return sorted[0].value;
  }

  /**
   * Retourne le type d'affichage des dimensions en fonction de la configuration
   * - 'global': Dimension unique pour tout le meuble (1 rangée ou 2 rangées sans allowOffset)
   * - 'perRow': Dimension par rangée avec contraintes de combinaison (2 rangées avec allowOffset)
   */
  getDimensionDisplayType(config: CompositionConfigurationDto): 'global' | 'perRow' {
    const rowCount = config.rows?.length ?? 0;

    // 1 rangée : affichage global
    if (rowCount <= 1) {
      return 'global';
    }

    // 2 rangées avec allowOffset true : affichage par rangée avec combinaisons
    if (config.allowOffset) {
      return 'perRow';
    }

    // 2 rangées avec allowOffset false : affichage global
    return 'global';
  }
}
